import { IStyle } from "./../../../Types/Style";
import { AxiosResponse } from "axios";
import { push } from "connected-react-router";
import * as R from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";

import { AxiosInterceptedResult } from "../../../api/interceptors";
import { IResolveError, IResolveResponse } from "../../../api/ResolveApi/resolve-api.types";
import {
  modifyObjectDeletedPatch,
  patchPayloadCreator,
} from "../../../components/course-author/helpers/patches/creators";
import { dependencies } from "../../../dependencies";
import {
  ICreationResponse,
  IFullOrganization,
  IFullStyle,
  IStateResponse,
  IStyleResponse,
  resolveStylePostSuccessResponseValueToStyle,
} from "../../../parsers/resolve";
import { SagaAction } from "../../../sagas/types";
import * as selectors from "../../../selectors/selectors";
import { FetchIdsAndResolve, fetchIdsAndResolveThem, FetchResult } from "../../Common/sagas";
import { ICompleteOrganization } from "../../types/common";
import { IFirstFetchPayload } from "./";

const { api } = dependencies;

type FetchStylesResult = FetchResult<{
  themesIdsResponse: AxiosResponse<string[]>;
  themesResponse: AxiosResponse<Array<IResolveResponse<IStyleResponse> | IResolveError>>;
}>;

export function* fetchStylesAndResolve(accountId: string, organizationId: string) {
  const fetchResolver = fetchIdsAndResolveThem(
    {
      getEditableByRelativeTo: api.styles.getEditableByRelativeTo,
      resolve: api.resolve.style.post,
    },
    {
      identityName: "styles",
      includeErrors: true,
    }
  );
  const [err, resp]: FetchIdsAndResolve = yield call(fetchResolver(accountId, organizationId));
  if (err || !resp) {
    return [err, resp];
  }
  const [ids, resolvedData] = resp;
  return [err, { themesIdsResponse: ids, themesResponse: resolvedData }];
}

interface IFurtherActions {
  onSuccess: ActionFunction1<IFirstFetchPayload, Action<IFirstFetchPayload>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const fetchEditableThemes = (fa: IFurtherActions) =>
  function* _fetchEditableThemes(): SagaAction {
    const accountId: string = yield select(selectors.accountId);
    const organizationId: string = yield select(selectors.organizationId);
    const [orgErr, orgResp]: AxiosInterceptedResult<Array<
      IResolveResponse<IStateResponse<IFullOrganization>>
    >> = yield call(api.resolve.organization.post, accountId, [organizationId]);
    if (orgErr || !orgResp || !orgResp.data.length) {
      yield put(fa.onFailure({ message: orgErr || "Could not resolve organization" }));
      return;
    }
    const firstOrg = orgResp.data[0];
    const fullOrganization: ICompleteOrganization = {
      organizationId,
      versionId: firstOrg.value.versionId,
      ...firstOrg.value.state,
    };
    const [err, resp]: FetchStylesResult = yield call(fetchStylesAndResolve, accountId, organizationId);
    if (err) {
      yield put(fa.onFailure(err));
      return;
    }
    if (!resp) {
      yield put(fa.onFailure({ message: "Empty response when fetching themes" }));
      return;
    }
    const { themesIdsResponse, themesResponse } = resp;
    const themes: IStyle[] = R.zip(themesIdsResponse.data, themesResponse.data)
      .filter(
        ([_, data]) => data.code === 200 && (data as IResolveResponse<IStyleResponse>).value.state.course === null
      )
      .map(([id, data]) => resolveStylePostSuccessResponseValueToStyle(id, data as IResolveResponse<IStyleResponse>));
    yield put(
      fa.onSuccess({
        fullOrganization,
        styles: themes,
      })
    );
    return themes;
  };

interface IDeleteFurtherActions {
  onSuccess: ActionFunction1<IStyle[], Action<IStyle[]>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const deleteTheme = (fa: IDeleteFurtherActions) =>
  function* _deleteTheme(action: Action<string>): SagaAction {
    const accountId = yield select(selectors.accountId);
    const styleId = action.payload;
    const oldThemes: IStyle[] = yield select(selectors.themes);
    const themesToDelete: IStyle[] = oldThemes.filter(s => s.styleId === styleId);
    if (themesToDelete.length === 0) {
      return yield put(fa.onFailure({ message: "Style requested to delete not found." }));
    }
    const versionId = themesToDelete[0].versionId;
    const deletePatch = yield call(modifyObjectDeletedPatch, versionId, accountId);
    const patchPayload = yield call(patchPayloadCreator, versionId, deletePatch);
    const [patchError] = yield call(api.styles.patchStyle, accountId, styleId, patchPayload);
    if (patchError) {
      return yield put(
        fa.onFailure({
          message: "Failed to patch theme as deleted.",
        })
      );
    }
    const themes = oldThemes.filter(s => s.styleId !== styleId);
    yield put(fa.onSuccess(themes));
  };

interface IAddNewThemeFurtherActions {
  onSuccess: ActionFunction1<void, Action<void>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

type OrgId = string;

const mkDefaultStyle = (orgOwnerId: OrgId, custom?: Partial<IFullStyle>): IFullStyle => ({
  assets: {
    elems: [],
    nextId: 0,
  },
  bodyFontFamilyId: null,
  course: null,
  deleted: false,
  displayName: "New Theme",
  headerFontFamilyId: null,
  lessCode: "",
  logo: null,
  logoImageId: null,
  monospaceFontFamilyId: null,
  owner: orgOwnerId,
  ...(custom || {}),
});

export const addNewTheme = (fa: IAddNewThemeFurtherActions) =>
  function* _addNewTheme(): SagaAction {
    const accountId = yield select(selectors.accountId);
    const organizationId = yield select(selectors.organizationId);
    const defaultFullStyle = mkDefaultStyle(organizationId);
    const [err, resp]: AxiosInterceptedResult<ICreationResponse<IFullStyle>> = yield call(
      api.styles.create,
      defaultFullStyle,
      accountId
    );
    if (err) {
      yield put(fa.onFailure({ message: "Fail to add new theme" }));
      return;
    }
    if (!resp) {
      yield put(fa.onFailure({ message: "Fail to add new theme" }));
      return;
    }
    yield put(push(`/theme-management/${resp.data.value}`));
    yield put(fa.onSuccess());
  };
